import * as React from 'react'

import type { Group } from 'types/group'

import { ContextMenuTarget } from 'components/ds'

import { useExplorerContextMenusEnabled } from 'hooks/use-explorer-context-menus-enabled'

import GroupMenuBody from '../group-menu-body'
import { useGroupMenu } from '../use-group-menu'

type Props = {
  children: React.ReactNode
  group: Group | null | undefined
  groupDepth: number
}

/**
 * Opens the folder/channel's three-dot menu on right-click anywhere in
 * `children`.
 */
const GroupContextMenu = ({
  children,
  group,
  groupDepth,
}: Props): React.ReactNode => {
  const contextMenusEnabled = useExplorerContextMenusEnabled()

  // Skip useGroupMenu's selectors/state entirely while the flag is off,
  // rather than calling it on every row and disabling ContextMenuTarget after.
  if (!contextMenusEnabled || !group) return children

  return (
    <GroupContextMenuContent group={group} groupDepth={groupDepth}>
      {children}
    </GroupContextMenuContent>
  )
}

const GroupContextMenuContent = ({
  children,
  group,
  groupDepth,
}: {
  children: React.ReactNode
  group: Group
  groupDepth: number
}): React.ReactNode => {
  const { options, handleMenuAction, handleMenuOpen, dialogs } = useGroupMenu({
    group,
    groupDepth,
  })

  return (
    <>
      <ContextMenuTarget
        onOpenChange={open => {
          if (open) handleMenuOpen()
        }}
        menu={
          <GroupMenuBody options={options} onOptionClick={handleMenuAction} />
        }
      >
        {children}
      </ContextMenuTarget>
      {dialogs}
    </>
  )
}

export default GroupContextMenu
